import { useState } from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import {
  AllowanceDataFragment,
  BulkAssignEmployeesTimeoffPolicyMappingsInput,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import SelectField from 'modules/core/components/SelectField'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  policyId: number
  allowancesToMap: AllowanceDataFragment[]
  timeoffPolicyAllowances: AllowanceDataFragment[]
  onMappingsChange: (mappings: BulkAssignEmployeesTimeoffPolicyMappingsInput[]) => void
}

const toSelectOption = (allowance: AllowanceDataFragment) => ({
  label: allowance.name,
  value: allowance.id,
})

const AllowanceMapper = ({
  policyId,
  allowancesToMap,
  timeoffPolicyAllowances,
  onMappingsChange,
}: Props) => {
  const { navigation } = useNavigation()
  const [mappings, setMappings] = useState<BulkAssignEmployeesTimeoffPolicyMappingsInput[]>(
    allowancesToMap.map((allowance) => ({
      originalAllowanceId: allowance.id,
      newAllowanceId: 0,
      _destroy: true,
    }))
  )

  const hourlyAllowancesOptions: { label: string; value: number }[] = []
  const dailyAllowancesOptions: { label: string; value: number }[] = []

  timeoffPolicyAllowances.forEach((allowance) => {
    if (allowance.allowanceType === 'hours') {
      hourlyAllowancesOptions.push(toSelectOption(allowance))
    } else {
      dailyAllowancesOptions.push(toSelectOption(allowance))
    }
  })

  // TODO: this label is not translated
  const ignoreOption = { label: 'Ignore', value: -1 }
  if (dailyAllowancesOptions.length !== 0) {
    dailyAllowancesOptions.unshift(ignoreOption)
  }

  if (hourlyAllowancesOptions.length !== 0) {
    hourlyAllowancesOptions.unshift(ignoreOption)
  }

  const setNewAllowance = (chosenAllowanceId: string, oldAllowanceId: number) => {
    const newMappings = mappings.map((mapping) => {
      if (mapping.originalAllowanceId === oldAllowanceId) {
        return {
          ...mapping,
          newAllowanceId: chosenAllowanceId === '' ? 0 : parseInt(chosenAllowanceId, 10),
          _destroy: false,
        }
      }
      return mapping
    })
    setMappings(newMappings)
    onMappingsChange(newMappings)
  }

  let allowanceTypeMissing = false
  const findAllowanceType = (allowance: AllowanceDataFragment) => {
    if (allowance.allowanceType === 'hours') {
      if (hourlyAllowancesOptions.length === 0) {
        allowanceTypeMissing = true
        return []
      }
      return hourlyAllowancesOptions
    }
    if (allowance.allowanceType === 'days') {
      if (dailyAllowancesOptions.length === 0) {
        allowanceTypeMissing = true
        return []
      }
      return dailyAllowancesOptions
    }
    throw new Error('Invalid allowance type')
  }

  return (
    <>
      <Box paddingBottom="s16">
        <Text size="200" weight="normal" color="grey800">
          {i18n.t('settings.timeoff_policies.manual_policy_mapper.multiple_allowances')}
        </Text>
      </Box>
      <Box
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceEvenly"
        gap="s8"
        paddingTop="s24"
      >
        <Box minWidth="s240" paddingLeft="s4">
          <Text size="300" weight="medium" color="darker800">
            {i18n.t('settings.timeoff_policies.manual_policy_mapper.previous_allowance')}
          </Text>
        </Box>
        <Box width="s200">
          <Text size="300" weight="medium" color="darker800">
            {i18n.t('settings.timeoff_policies.manual_policy_mapper.transfer_to')}
          </Text>
        </Box>
      </Box>
      {allowancesToMap.map((allowance) => {
        return (
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="spaceEvenly"
            gap="s8"
            paddingTop="s24"
            key={allowance.id}
          >
            <Box minWidth="s200">
              <Text size="300" weight="normal">
                {allowance.name}
              </Text>
            </Box>
            <Box alignItems="center">
              <Icon.Medium icon="arrow-right" />
            </Box>
            <Box width="s200">
              {findAllowanceType(allowance).length === 0 ? (
                <Box paddingLeft="s6">
                  <Text size="300" weight="normal" color="radicalRed800">
                    {allowance.allowanceType === 'hours'
                      ? i18n.t(
                          'settings.timeoff_policies.manual_policy_mapper.hourly_allowance_missing'
                        )
                      : i18n.t(
                          'settings.timeoff_policies.manual_policy_mapper.daily_allowance_missing'
                        )}
                  </Text>
                </Box>
              ) : (
                <SelectField
                  options={findAllowanceType(allowance) ?? []}
                  defaultOption={{
                    value: '',
                    label: i18n.t('common.select'),
                  }}
                  key={allowance.id}
                  showOriginalLabelText
                  mandatory
                  onChange={(value) => setNewAllowance(value, allowance.id)}
                />
              )}
            </Box>
          </Box>
        )
      })}
      {allowanceTypeMissing && !!policyId && (
        <Box paddingTop="s24">
          <Callout
            type="info"
            title={i18n.t('settings.timeoff_policies.manual_policy_mapper.title')}
            description={i18n.t('settings.timeoff_policies.manual_policy_mapper.description')}
            action={
              <Button.Router
                to={navigation.settings.timeoffPolicies.id.details.addAllowance.fullPath({
                  id: policyId,
                })}
                type="secondary"
              >
                {i18n.t('settings.timeoff_policies.manual_policy_mapper.link_text')}
              </Button.Router>
            }
            onRemove={() => {
              return null
            }}
          />
        </Box>
      )}
    </>
  )
}

export default observer(AllowanceMapper)
